import { z } from 'zod';
import type {
  ShipmentHeader,
  ShipmentReportDailyRow,
  ShipmentReportTotalRow,
  ShipmentReportRowValues,
  ShipmentReceiverSelection,
  ShipmentScaleTicket,
  ShipmentTotalsSummary,
} from './shipments-shared.dto.js';
import {
  CounterpartySelectionSchema,
  CounterpartySnapshotSchema,
} from '../counterparties/counterparties.schema.js';
import {
  IsoDateTimeSchema,
  ShipmentScaleTypeSchema,
  ShipmentStatusSchema,
  ShipmentTargetScenarioSchema,
} from './shipments-types.js';
import { ReportableWeightLbSchema } from '../reportable-weight.schema.js';

const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntSchema = z.number().int().min(0);
const positiveNumberSchema = z.number().gt(0);
const allowancePctSchema = z.number().min(0).max(100);
const trimmedStringSchema = z.string().trim().min(1);

const validateRangeOrder = (value: unknown, ctx: z.RefinementCtx): void => {
  if (!value || typeof value !== 'object') {
    return;
  }
  const rangeStartAt = (value as { rangeStartAt?: string }).rangeStartAt;
  const rangeEndAt = (value as { rangeEndAt?: string }).rangeEndAt;
  if (typeof rangeStartAt !== 'string' || typeof rangeEndAt !== 'string') {
    return;
  }
  const rangeStartMs = Date.parse(rangeStartAt);
  const rangeEndMs = Date.parse(rangeEndAt);
  if (!Number.isNaN(rangeStartMs) && !Number.isNaN(rangeEndMs) && rangeStartMs >= rangeEndMs) {
    ctx.addIssue({
      code: 'custom',
      message: 'rangeStartAt must be before rangeEndAt.',
      path: ['rangeEndAt'],
    });
  }
};
export const validateScaleTicketChronology = (value: unknown, ctx: z.RefinementCtx): void => {
  if (!value || typeof value !== 'object') {
    return;
  }
  const createdAt = (value as { createdAt?: string }).createdAt;
  const updatedAt = (value as { updatedAt?: string }).updatedAt;
  if (typeof createdAt === 'string' && typeof updatedAt === 'string') {
    const createdMs = Date.parse(createdAt);
    const updatedMs = Date.parse(updatedAt);
    if (!Number.isNaN(createdMs) && !Number.isNaN(updatedMs) && createdMs > updatedMs) {
      ctx.addIssue({
        code: 'custom',
        message: 'createdAt must be on or before updatedAt.',
        path: ['updatedAt'],
      });
    }
  }
  const voidedAt = (value as { voidedAt?: string | null }).voidedAt;
  if (typeof voidedAt === 'string' && typeof updatedAt === 'string') {
    const voidedMs = Date.parse(voidedAt);
    const updatedMs = Date.parse(updatedAt);
    if (!Number.isNaN(voidedMs) && !Number.isNaN(updatedMs) && voidedMs < updatedMs) {
      ctx.addIssue({
        code: 'custom',
        message: 'voidedAt must be on or after updatedAt.',
        path: ['voidedAt'],
      });
    }
  }
};

export const validateScaleTicketWeights = (value: unknown, ctx: z.RefinementCtx): void => {
  if (!value || typeof value !== 'object') {
    return;
  }

  const gross = (value as { grossWeightLb?: number | null }).grossWeightLb;
  const tare = (value as { tareWeightLb?: number | null }).tareWeightLb;
  const net = (value as { netWeightLb?: number | null }).netWeightLb;
  const isComplete = (value as { isComplete?: boolean }).isComplete;

  if (typeof tare === 'number' && !Number.isNaN(tare)) {
    if (typeof gross !== 'number' || Number.isNaN(gross)) {
      ctx.addIssue({
        code: 'custom',
        message: 'grossWeightLb is required when tareWeightLb is provided.',
        path: ['grossWeightLb'],
      });
      return;
    }

    if (gross < tare) {
      ctx.addIssue({
        code: 'custom',
        message: 'grossWeightLb must be greater than or equal to tareWeightLb.',
        path: ['grossWeightLb'],
      });
      return;
    }

    if (typeof net === 'number' && !Number.isNaN(net)) {
      const expectedNet = gross - tare;
      if (Math.abs(net - expectedNet) > 1e-9) {
        ctx.addIssue({
          code: 'custom',
          message: 'netWeightLb must equal grossWeightLb - tareWeightLb.',
          path: ['netWeightLb'],
        });
      }
    }
  }

  if (typeof isComplete === 'boolean') {
    const hasNet = typeof net === 'number' && !Number.isNaN(net);
    if (isComplete !== hasNet) {
      ctx.addIssue({
        code: 'custom',
        message: 'isComplete must match netWeightLb presence.',
        path: ['isComplete'],
      });
    }
  }
};

const shipmentSourceEvidenceShape = {
  sourceLoadSessionId: z.uuid().nullable().optional(),
  sourceLoadSessionGroupId: z.uuid().nullable().optional(),
};

export const validateShipmentSourceEvidencePair = (value: unknown, ctx: z.RefinementCtx): void => {
  if (!value || typeof value !== 'object') {
    return;
  }

  const sourceLoadSessionId = (value as { sourceLoadSessionId?: string | null }).sourceLoadSessionId;
  const sourceLoadSessionGroupId =
    (value as { sourceLoadSessionGroupId?: string | null }).sourceLoadSessionGroupId;

  const hasLoadSessionId = sourceLoadSessionId !== undefined && sourceLoadSessionId !== null;
  const hasGroupId = sourceLoadSessionGroupId !== undefined && sourceLoadSessionGroupId !== null;
  if (hasLoadSessionId === hasGroupId) {
    return;
  }

  ctx.addIssue({
    code: 'custom',
    message: 'sourceLoadSessionId and sourceLoadSessionGroupId must be provided together.',
    path: [hasLoadSessionId ? 'sourceLoadSessionGroupId' : 'sourceLoadSessionId'],
  });
};

export const ShipmentTotalsSummarySchema: z.ZodType<ShipmentTotalsSummary> = z
  .object({
    swPhysicalLb: ReportableWeightLbSchema,
    swCrvLb: ReportableWeightLbSchema,
    redemptionWeightLb: ReportableWeightLbSchema,
    scrapWeightLb: ReportableWeightLbSchema,
    totalWeightLb: ReportableWeightLbSchema,
    refundValueCents: nonNegativeIntSchema,
    qualityDiscountMoneyCentsTotal: nonNegativeIntSchema,
    qualityDiscountEquivalentLbs: ReportableWeightLbSchema,
  })
  .strict();

const ShipmentReportRowValuesSchemaBase = z
  .object({
    swLb: ReportableWeightLbSchema,
    sclCount: nonNegativeIntSchema,
    scsCount: nonNegativeIntSchema,
    sc25Count: nonNegativeIntSchema,
    scrapWeightLb: ReportableWeightLbSchema,
    totalWeightLb: ReportableWeightLbSchema,
    refundValueCents: nonNegativeIntSchema,
    redemptionWeightLb: ReportableWeightLbSchema,
    discountCents: nonNegativeIntSchema,
  })
  .strict();

export const ShipmentReportRowValuesSchema: z.ZodType<ShipmentReportRowValues> =
  ShipmentReportRowValuesSchemaBase;

export const ShipmentReportDailyRowSchema: z.ZodType<ShipmentReportDailyRow> =
  ShipmentReportRowValuesSchemaBase.extend({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    hasDiscountAdjustment: z.boolean(),
  }).strict();

export const ShipmentReportTotalRowSchema: z.ZodType<ShipmentReportTotalRow> =
  ShipmentReportRowValuesSchemaBase.extend({
    label: z.literal('Total'),
    shippingReportNumber: trimmedStringSchema.nullable(),
    receivedWeightLb: ReportableWeightLbSchema.nullable(),
  }).strict();

const shipmentRangeBuilderShape = {
  mode: z.literal('range'),
  rcScopeId: z.uuid(),
  materialId: z.uuid(),
  rangeStartAt: IsoDateTimeSchema,
  rangeEndAt: IsoDateTimeSchema,
};

export const ShipmentRangeBuilderInputSchema = z.object(shipmentRangeBuilderShape).strict();

export const ShipmentTargetBuilderInputSchema = z
  .object({
    mode: z.literal('target'),
    rcScopeId: z.uuid(),
    materialId: z.uuid(),
    rangeStartAt: IsoDateTimeSchema,
    rangeEndAt: IsoDateTimeSchema,
    targetLb: positiveNumberSchema,
    allowancePct: allowancePctSchema.optional().default(2.5),
    chosenScenario: ShipmentTargetScenarioSchema,
  })
  .strict();

export const ShipmentBuilderInputSchema = z
  .discriminatedUnion('mode', [ShipmentRangeBuilderInputSchema, ShipmentTargetBuilderInputSchema])
  .superRefine(validateRangeOrder);

export const ShipmentSourceEvidenceSchema = z
  .object(shipmentSourceEvidenceShape)
  .strict()
  .superRefine(validateShipmentSourceEvidencePair);

const ShipmentRangeBuilderIntentSchema = z.object(shipmentRangeBuilderShape).strict();

const ShipmentTargetBuilderIntentSchema = z
  .object({
    mode: z.literal('target'),
    rcScopeId: z.uuid(),
    materialId: z.uuid(),
    rangeStartAt: IsoDateTimeSchema,
    rangeEndAt: IsoDateTimeSchema,
    targetLb: positiveNumberSchema,
    allowancePct: allowancePctSchema,
    chosenScenario: ShipmentTargetScenarioSchema,
  })
  .strict();

export const ShipmentBuilderIntentSchema = z
  .discriminatedUnion('mode', [ShipmentRangeBuilderIntentSchema, ShipmentTargetBuilderIntentSchema])
  .superRefine(validateRangeOrder);

export const ShipmentHeaderSchema = z
  .object({
    shipmentId: z.uuid(),
    companyId: z.uuid(),
    stationId: z.uuid(),
    status: ShipmentStatusSchema,
    sourcePreparationRequestId: z.uuid().nullable().optional(),
    builderIntent: ShipmentBuilderIntentSchema,
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    departedAt: IsoDateTimeSchema.nullable().optional(),
    ...ShipmentSourceEvidenceSchema.shape,
  })
  .superRefine(validateShipmentSourceEvidencePair)
  .strict() satisfies z.ZodType<ShipmentHeader>;

export const ShipmentReceiverSelectionSchema: z.ZodType<ShipmentReceiverSelection> =
  CounterpartySelectionSchema;

export const ShipmentScaleTicketInputSchema = z
  .object({
    scaleType: ShipmentScaleTypeSchema,
    ticketNumber: trimmedStringSchema,
    weighedAt: IsoDateTimeSchema,
    grossWeightLb: nonNegativeNumberSchema,
    tareWeightLb: nonNegativeNumberSchema.nullable().optional(),
    notes: trimmedStringSchema.nullable().optional(),
    photoUrl: trimmedStringSchema.nullable().optional(),
  })
  .strict();

export const ShipmentScaleTicketCommonSchema = z
  .object({
    scaleTicketId: z.uuid(),
    scaleType: ShipmentScaleTypeSchema,
    ticketNumber: trimmedStringSchema,
    weighedAt: IsoDateTimeSchema,
    grossWeightLb: nonNegativeNumberSchema.nullable(),
    tareWeightLb: nonNegativeNumberSchema.nullable(),
    netWeightLb: nonNegativeNumberSchema.nullable(),
    isComplete: z.boolean(),
    notes: trimmedStringSchema.nullable(),
    photoUrl: trimmedStringSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    createdByUserId: z.uuid().nullable(),
    updatedAt: IsoDateTimeSchema,
    voidedAt: IsoDateTimeSchema.nullable(),
    voidedByUserId: z.uuid().nullable(),
    voidReason: trimmedStringSchema.nullable(),
  })
  .strict();

export const ShipmentScaleTicketSchema: z.ZodType<ShipmentScaleTicket> = z
  .object({
    ...ShipmentScaleTicketCommonSchema.shape,
    shipmentId: z.uuid(),
  })
  .strict()
  .superRefine(validateScaleTicketChronology)
  .superRefine(validateScaleTicketWeights);
